<script setup>
import { computed, onMounted, ref } from 'vue';
import { I18nT, useI18n } from 'vue-i18n';
import { Dropdown } from 'floating-vue';
import MessageTemplatesAPI from 'dashboard/api/messageTemplates';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import EmptyStateLayout from 'dashboard/components-next/EmptyStateLayout.vue';
import MessageTemplateFormDialog from './MessageTemplateFormDialog.vue';
import MessageTemplatePreviewDialog from './MessageTemplatePreviewDialog.vue';
import ConfirmMessageTemplateDeleteDialog from './ConfirmMessageTemplateDeleteDialog.vue';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const { t, te } = useI18n();

// Categorias vêm da Meta em inglês (UTILITY, AUTHENTICATION...); categoria desconhecida
// é exibida como veio.
const categoryLabel = category => {
  const key = `INBOX_MGMT.MESSAGE_TEMPLATES.CATEGORY_OPTIONS.${(category || '').toUpperCase()}`;
  return te(key) ? t(key) : category;
};

const isFetching = ref(false);
const templates = ref([]);
const formDialogRef = ref(null);
const previewDialogRef = ref(null);
const deleteDialogRef = ref(null);
const templatePendingDeletion = ref(null);
const isDeleting = ref(false);
const submittingId = ref(null);
const duplicatingId = ref(null);

const STATUS_STYLES = {
  draft: 'bg-n-slate-3 text-n-slate-11',
  pending: 'bg-n-amber-3 text-n-amber-11',
  approved: 'bg-n-teal-3 text-n-teal-11',
  rejected: 'bg-n-ruby-3 text-n-ruby-11',
  paused: 'bg-n-amber-3 text-n-amber-11',
  disabled: 'bg-n-ruby-3 text-n-ruby-11',
};

const fetchTemplates = async () => {
  isFetching.value = true;
  try {
    const { data } = await MessageTemplatesAPI.get(props.inbox.id);
    templates.value = data.payload;
  } catch (error) {
    useAlert(t('INBOX_MGMT.MESSAGE_TEMPLATES.FETCH_ERROR'));
  } finally {
    isFetching.value = false;
  }
};

onMounted(fetchTemplates);

// Templates that already exist on Meta (synced via inbox.message_templates) but were
// never created through the Corvus form — shown read-only, matched by name so an
// approved local draft isn't listed twice once it shows up in the synced array too.
const syncedOnlyTemplates = computed(() => {
  const localNames = new Set(templates.value.map(template => template.name));
  const syncedTemplates = Array.isArray(props.inbox.message_templates)
    ? props.inbox.message_templates
    : [];
  return syncedTemplates
    .filter(template => !localNames.has(template.name))
    .map(template => ({
      id: null,
      name: template.name,
      category: template.category,
      language: template.language,
      status: 'submitted',
      meta_status: template.status,
      components: template.components,
      readOnly: true,
    }));
});

const allTemplates = computed(() => [
  ...templates.value,
  ...syncedOnlyTemplates.value,
]);

const statusKey = template => {
  if (template.status === 'draft') return 'draft';
  return (template.meta_status || 'pending').toLowerCase();
};

const statusLabel = template =>
  t(`INBOX_MGMT.MESSAGE_TEMPLATES.STATUS.${statusKey(template).toUpperCase()}`);

// Meta's own page for requesting a manual review of a rejected template.
const SUPPORT_HOME_URL =
  'https://business.facebook.com/navigation/interstitial/?redirect_uri=https%3A%2F%2Fbusiness.facebook.com%2Fbusiness-support-home%2F1155461011704562%2F112124265101119%2F%3Ftemplates_tab%3Drejected_templates';

const isRejected = template => statusKey(template) === 'rejected';

// The non-rejected explanation is plain text — the rejected one is rendered as real
// markup (see <I18nT> in the template) since it embeds an actual clickable link.
const statusExplanation = template =>
  t(
    `INBOX_MGMT.MESSAGE_TEMPLATES.STATUS_EXPLANATION.${statusKey(template).toUpperCase()}`
  );

// Meta sends `reason` as a short code (e.g. "INVALID_FORMAT"), not a sentence — map
// the documented codes to a readable explanation. "NONE" means Meta gave no specific
// reason, so nothing is shown for it; an unrecognized future code falls back to itself
// rather than hiding real information from the user.
const REJECTION_REASON_CODES = [
  'ABUSIVE_CONTENT',
  'INCORRECT_CATEGORY',
  'INVALID_FORMAT',
  'SCAM',
  'TAG_CONTENT_MISMATCH',
  'TAG_CATEGORY_MISMATCH',
  'UNDEFINED',
];

const rejectionReasonText = template => {
  const code = template.rejection_reason?.toUpperCase();
  if (!code || code === 'NONE') return '';
  if (REJECTION_REASON_CODES.includes(code)) {
    return t(`INBOX_MGMT.MESSAGE_TEMPLATES.REJECTION_REASONS.${code}`);
  }
  return template.rejection_reason;
};

const statusClass = template =>
  STATUS_STYLES[statusKey(template)] || STATUS_STYLES.pending;

const openCreateDialog = () => formDialogRef.value?.open();

const openPreview = template => previewDialogRef.value?.open(template);

const onTemplateSaved = () => fetchTemplates();

const submitTemplate = async template => {
  submittingId.value = template.id;
  try {
    await MessageTemplatesAPI.submit(props.inbox.id, template.id);
    useAlert(t('INBOX_MGMT.MESSAGE_TEMPLATES.FORM.SUBMITTED'));
    await fetchTemplates();
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('INBOX_MGMT.MESSAGE_TEMPLATES.FORM.SAVE_ERROR')
    );
  } finally {
    submittingId.value = null;
  }
};

const duplicateTemplate = async template => {
  duplicatingId.value = template.id;
  try {
    await MessageTemplatesAPI.duplicate(props.inbox.id, template.id);
    useAlert(t('INBOX_MGMT.MESSAGE_TEMPLATES.DUPLICATE_SUCCESS'));
    await fetchTemplates();
  } catch (error) {
    useAlert(t('INBOX_MGMT.MESSAGE_TEMPLATES.DUPLICATE_ERROR'));
  } finally {
    duplicatingId.value = null;
  }
};

const askDeleteConfirmation = template => {
  templatePendingDeletion.value = template;
  deleteDialogRef.value?.open();
};

const confirmDelete = async () => {
  if (!templatePendingDeletion.value) return;

  isDeleting.value = true;
  try {
    await MessageTemplatesAPI.destroy(
      props.inbox.id,
      templatePendingDeletion.value.id
    );
    useAlert(t('INBOX_MGMT.MESSAGE_TEMPLATES.DELETE.SUCCESS'));
    deleteDialogRef.value?.close();
    await fetchTemplates();
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('INBOX_MGMT.MESSAGE_TEMPLATES.DELETE.ERROR')
    );
  } finally {
    isDeleting.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex items-center justify-between">
      <h3 class="text-base font-medium text-n-slate-12">
        {{ t('INBOX_MGMT.MESSAGE_TEMPLATES.TITLE') }}
      </h3>
      <Button
        icon="i-lucide-plus"
        :label="t('INBOX_MGMT.MESSAGE_TEMPLATES.CREATE')"
        @click="openCreateDialog"
      />
    </div>

    <div v-if="isFetching" class="flex justify-center py-10">
      <Spinner class="size-6" />
    </div>

    <EmptyStateLayout
      v-else-if="!allTemplates.length"
      :title="t('INBOX_MGMT.MESSAGE_TEMPLATES.EMPTY_STATE.TITLE')"
      :subtitle="t('INBOX_MGMT.MESSAGE_TEMPLATES.EMPTY_STATE.SUBTITLE')"
      :show-backdrop="false"
    />

    <div v-else class="flex flex-col gap-2">
      <CardLayout
        v-for="template in allTemplates"
        :key="template.id || template.name"
        layout="row"
      >
        <div
          class="grid w-full min-w-0 grid-cols-[minmax(0,1fr)_7rem_5rem_7rem_18rem] items-center gap-4"
        >
          <span class="min-w-0 truncate text-sm font-medium text-n-slate-12">
            {{ template.name }}
          </span>
          <span class="min-w-0 truncate text-sm text-n-slate-11">
            {{ categoryLabel(template.category) }}
          </span>
          <span class="min-w-0 truncate text-sm text-n-slate-11">
            {{ template.language }}
          </span>
          <Dropdown
            theme="tooltip"
            :triggers="['hover']"
            :popper-triggers="['hover']"
            placement="top"
          >
            <span
              class="w-fit cursor-default rounded-full px-2 py-0.5 text-xs font-medium"
              :class="statusClass(template)"
            >
              {{ statusLabel(template) }}
            </span>
            <template #popper>
              <div class="max-w-xs p-1 text-xs text-white">
                <I18nT
                  v-if="isRejected(template)"
                  keypath="INBOX_MGMT.MESSAGE_TEMPLATES.STATUS_EXPLANATION.REJECTED"
                  tag="p"
                  class="m-0"
                >
                  <template #linkText>
                    <a
                      :href="SUPPORT_HOME_URL"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="underline text-white"
                    >
                      {{
                        t('INBOX_MGMT.MESSAGE_TEMPLATES.SUPPORT_HOME_LINK_TEXT')
                      }}
                    </a>
                  </template>
                </I18nT>
                <p v-else class="m-0">{{ statusExplanation(template) }}</p>
                <p v-if="rejectionReasonText(template)" class="m-0 mt-1">
                  {{ t('INBOX_MGMT.MESSAGE_TEMPLATES.REJECTION_REASON_LABEL') }}
                  {{ rejectionReasonText(template) }}
                </p>
              </div>
            </template>
          </Dropdown>

          <div class="flex items-center justify-end gap-2">
            <Button
              v-tooltip.top-end="
                t('INBOX_MGMT.MESSAGE_TEMPLATES.PREVIEW_TOOLTIP')
              "
              size="xs"
              variant="faded"
              color="slate"
              icon="i-lucide-eye"
              @click="openPreview(template)"
            />
            <Button
              v-if="!template.readOnly && template.status === 'draft'"
              size="xs"
              variant="faded"
              :is-loading="submittingId === template.id"
              :label="t('INBOX_MGMT.MESSAGE_TEMPLATES.SUBMIT')"
              @click="submitTemplate(template)"
            />
            <Button
              v-if="!template.readOnly"
              size="xs"
              variant="faded"
              color="slate"
              :is-loading="duplicatingId === template.id"
              :label="t('INBOX_MGMT.MESSAGE_TEMPLATES.DUPLICATE')"
              @click="duplicateTemplate(template)"
            />
            <Button
              v-if="!template.readOnly"
              size="xs"
              variant="faded"
              color="ruby"
              icon="i-lucide-trash"
              @click="askDeleteConfirmation(template)"
            />
          </div>
        </div>
      </CardLayout>
    </div>

    <MessageTemplateFormDialog
      ref="formDialogRef"
      :inbox-id="inbox.id"
      @saved="onTemplateSaved"
    />
    <MessageTemplatePreviewDialog ref="previewDialogRef" />
    <ConfirmMessageTemplateDeleteDialog
      ref="deleteDialogRef"
      :template="templatePendingDeletion"
      :is-loading="isDeleting"
      @confirm="confirmDelete"
    />
  </div>
</template>
